import { useState, useEffect } from "react";
import { Trash2 } from "lucide-react";
import { Button } from "../../../../components/tailwind/button";
import { Label } from "../../../../components/tailwind/label";
import { Input } from "../../../../components/tailwind/input";
import { inspectorInputClass, errorInputStyle } from "./RightInspectorStyles";
import { keyValueToRows } from "./KeyValueField";
import { getSaveToContextValuePlaceholder } from "../../utils/workflowInspectorUx";

export function SaveToContextFields({ data, onUpdate, paletteType, workflowType }) {
  const [rows, setRows] = useState(() => {
    const val = data.saveToContext;
    if (Array.isArray(val)) return val;
    return keyValueToRows(val);
  });
  const [error, setError] = useState("");

  useEffect(() => {
    setError("");
    const externalRows = Array.isArray(data.saveToContext)
      ? data.saveToContext
      : keyValueToRows(data.saveToContext);
    
    // Check if the external non-empty rows differ from our local non-empty rows
    const extNonEmpty = externalRows.filter(r => r.key);
    const localNonEmpty = rows.filter(r => r.key);
    
    const hasExternalDiff = extNonEmpty.length !== localNonEmpty.length || 
      extNonEmpty.some((extRow, i) => {
        const localRow = localNonEmpty[i];
        return !localRow || localRow.key !== extRow.key || localRow.value !== extRow.value;
      });

    if (hasExternalDiff) {
      setRows(externalRows);
    }
  }, [data.saveToContext]);

  const isHttpRequest = data.config?.action === "HTTP_REQUEST";
  const valuePlaceholder = getSaveToContextValuePlaceholder(paletteType, workflowType);

  const handleRowChange = (idx, field, val) => {
    setError("");
    const next = [...rows];
    next[idx] = { ...next[idx], [field]: val };
    setRows(next);
    onUpdate({
      ...data,
      saveToContext: next,
      hasError: false
    });
  };

  const handleAddRow = () => {
    const hasIncomplete = rows.some(row => !row.key.trim() || !row.value.trim());
    if (hasIncomplete) {
      setError("Please fill in all names and results before adding a new value.");
      return;
    }
    setError("");
    const next = [...rows, { key: "", value: "" }];
    setRows(next);
    onUpdate({
      ...data,
      saveToContext: next,
      hasError: false
    });
  };

  const handleRemoveRow = (idx) => {
    setError("");
    const next = rows.filter((_, i) => i !== idx);
    setRows(next);
    onUpdate({
      ...data,
      saveToContext: next,
      hasError: false
    });
  };

  return (
    <div className="space-y-3 border-t border-gray-100 dark:border-slate-800 pt-4">
      <Label className="text-xs font-semibold text-gray-700 dark:text-gray-300">Remember this result for later steps</Label>
      <p className="text-xs text-gray-400 dark:text-gray-500">Optional. Give the result a simple name so later steps can use it.</p>
      {rows.map((row, idx) => {
        const isKeyError = data.hasError && (!row.key || !row.key.trim());
        const isValError = data.hasError && (!row.value || !row.value.trim());
        return (
          <div className="flex items-center gap-2" key={`save-ctx-${idx}`}>
            <div className="grid grid-cols-2 gap-2 flex-1">
              <Input
                value={row.key}
                placeholder="Name (e.g. orderStatus)"
                className={inspectorInputClass}
                style={isKeyError ? errorInputStyle : undefined}
                onChange={(e) => handleRowChange(idx, "key", e.target.value)}
              />
              <Input
                value={
                  isHttpRequest
                    ? (row.value && row.value.startsWith("$result.") ? row.value.slice(8) : (row.value || ""))
                    : (row.value || "")
                }
                placeholder={valuePlaceholder}
                className={inspectorInputClass}
                style={isValError ? errorInputStyle : undefined}
                onChange={(e) => {
                  let val = e.target.value;
                  if (isHttpRequest && val && !val.startsWith("$")) {
                    val = `$result.${val}`;
                  }
                  handleRowChange(idx, "value", val);
                }}
              />
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-950/40 shrink-0"
              onClick={() => handleRemoveRow(idx)}
              aria-label="Remove remembered value"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        );
      })}
      <Button
        type="button"
        variant="outline"
        className="w-full h-8 text-xs"
        onClick={handleAddRow}
      >
        Add remembered value
      </Button>
      {error && (
        <p className="text-xs text-red-500 font-medium dark:text-red-400 mt-1">
          {error}
        </p>
      )}
    </div>
  );
}
